import type { FilterAction, FilterField, FilterRuleCreateInput, MailboxFilterRule } from "@cdt/shared";
import {
  FILTER_ACTION_LABELS,
  FILTER_ACTIONS,
  FILTER_FIELD_LABELS,
  FILTER_FIELDS,
} from "@cdt/shared";
import { Flag, Shield, Trash2 } from "lucide-react";
import { useState } from "react";

import { EventsErrorState } from "@/components/events/EventsErrorState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  useCreateFilterRule,
  useDeleteFilterRule,
  useUpdateFilterRule,
} from "@/hooks/useFilterRuleActions";
import { useFilterRulesQuery } from "@/hooks/useFilterRulesQuery";

const FIELD_PLACEHOLDER: Record<FilterField, string> = {
  FROM_ADDRESS: "Es. newsletter@mailchimp.com",
  FROM_DOMAIN: "Es. @mailchimp.com  oppure  sendgrid.net",
  SUBJECT_CONTAINS: "Es. offerta  oppure  unsubscribe",
};

function ActionBadge({ action }: { action: FilterAction }) {
  if (action === "FLAG_IMPORTANT") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-xs font-medium text-rose-700 ring-1 ring-inset ring-rose-200 dark:bg-rose-900/20 dark:text-rose-300 dark:ring-rose-700">
        <Flag className="h-3 w-3" />
        {FILTER_ACTION_LABELS[action]}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground ring-1 ring-inset ring-border">
      <Shield className="h-3 w-3" />
      {FILTER_ACTION_LABELS[action]}
    </span>
  );
}

export function FiltersPage() {
  const query = useFilterRulesQuery();
  const createMutation = useCreateFilterRule();
  const updateMutation = useUpdateFilterRule();
  const deleteMutation = useDeleteFilterRule();

  const [field, setField] = useState<FilterField>("FROM_DOMAIN");
  const [pattern, setPattern] = useState("");
  const [action, setAction] = useState<FilterAction>("IGNORE");

  const isInitialLoading = query.isLoading && query.data === undefined;

  function resetForm() {
    setPattern("");
    setAction("IGNORE");
  }

  async function submit(): Promise<void> {
    const payload: FilterRuleCreateInput = {
      field,
      pattern: pattern.trim(),
      action,
    };
    await createMutation.mutateAsync(payload);
    resetForm();
  }

  async function toggleActive(rule: MailboxFilterRule): Promise<void> {
    await updateMutation.mutateAsync({
      id: rule.id,
      patch: { isActive: !rule.isActive },
    });
  }

  async function onDelete(id: string): Promise<void> {
    await deleteMutation.mutateAsync({ id });
  }

  return (
    <div className="container space-y-6 py-8">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Filtri email</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Le regole di filtro attive vengono valutate su ogni email in arrivo.{" "}
          <strong>Ignora</strong>: l'email viene scartata prima dell'AI (costo zero),
          salvata come evento <span className="font-medium">Ignorato</span>.{" "}
          <strong>Segnala importante</strong>: l'email viene processata normalmente
          dall'AI ma l'evento risultante viene marcato come{" "}
          <span className="inline-flex items-center gap-1 font-medium text-rose-600">
            <Flag className="h-3 w-3" />
            importante
          </span>{" "}
          e filtabile nella lista.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Nuova regola</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-3">
          <div className="space-y-2">
            <Label htmlFor="filter-field">Criterio</Label>
            <select
              id="filter-field"
              value={field}
              onChange={(e) => setField(e.target.value as FilterField)}
              className="h-10 w-full rounded-md border bg-background px-3 text-sm"
            >
              {FILTER_FIELDS.map((f) => (
                <option key={f} value={f}>
                  {FILTER_FIELD_LABELS[f]}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="filter-action">Azione</Label>
            <select
              id="filter-action"
              value={action}
              onChange={(e) => setAction(e.target.value as FilterAction)}
              className="h-10 w-full rounded-md border bg-background px-3 text-sm"
            >
              {FILTER_ACTIONS.map((a) => (
                <option key={a} value={a}>
                  {FILTER_ACTION_LABELS[a]}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="filter-pattern">Pattern</Label>
            <Input
              id="filter-pattern"
              value={pattern}
              onChange={(e) => setPattern(e.target.value)}
              placeholder={FIELD_PLACEHOLDER[field]}
              onKeyDown={(e) => {
                if (e.key === "Enter" && pattern.trim()) void submit();
              }}
            />
            <p className="text-xs text-muted-foreground">
              {field === "FROM_ADDRESS" && "Indirizzo email esatto del mittente (case-insensitive)."}
              {field === "FROM_DOMAIN" && (
                <>
                  Dominio del mittente. Con o senza <code className="font-mono">@</code> iniziale.
                  Matcha tutti i mittenti di quel dominio.
                </>
              )}
              {field === "SUBJECT_CONTAINS" && "Sottostringa dell'oggetto (case-insensitive). Basta che sia contenuta."}
            </p>
          </div>
          <div className="md:col-span-3">
            <Button
              type="button"
              onClick={() => void submit()}
              disabled={pattern.trim().length === 0 || createMutation.isPending}
            >
              Aggiungi regola
            </Button>
          </div>
        </CardContent>
      </Card>

      {isInitialLoading ? (
        <Card>
          <CardContent className="py-8 text-sm text-muted-foreground">
            Caricamento regole...
          </CardContent>
        </Card>
      ) : query.isError ? (
        <EventsErrorState
          message={query.error instanceof Error ? query.error.message : undefined}
          onRetry={() => void query.refetch()}
        />
      ) : query.data && query.data.items.length > 0 ? (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full min-w-[540px] text-sm">
            <thead>
              <tr className="border-b bg-muted/40">
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Criterio</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Pattern</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Azione</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Stato</th>
                <th className="px-4 py-3 text-right font-medium text-muted-foreground">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {query.data.items.map((rule) => (
                <tr key={rule.id} className="border-b last:border-0 hover:bg-muted/20">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Shield className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <span className="font-medium">{FILTER_FIELD_LABELS[rule.field]}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
                      {rule.pattern}
                    </code>
                  </td>
                  <td className="px-4 py-3">
                    <ActionBadge action={rule.action} />
                  </td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => void toggleActive(rule)}
                      disabled={updateMutation.isPending}
                      className="focus:outline-none"
                      title={rule.isActive ? "Disattiva regola" : "Attiva regola"}
                    >
                      <Badge variant={rule.isActive ? "default" : "outline"}>
                        {rule.isActive ? "Attiva" : "Disattivata"}
                      </Badge>
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                      disabled={deleteMutation.isPending && deleteMutation.variables?.id === rule.id}
                      onClick={() => void onDelete(rule.id)}
                      title="Elimina regola"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      <span className="sr-only">Elimina</span>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <Card>
          <CardContent className="py-8 text-sm text-muted-foreground">
            Nessuna regola configurata. Aggiungi una regola per iniziare a filtrare le email.
          </CardContent>
        </Card>
      )}
    </div>
  );
}
